<template>
  <AppPage class="work">
    <WorkPanel :data="work?.data" />
    <WorkContent :medias="work?.data?.media" />
    <WorkThumbs :medias="work?.data?.media" />
    <WorkPanelNext :nextProject="nextProject" />
    <WorkNextMobile :nextProject="nextProject" v-show="$mq.isMobile" />
    <AppFooter :page="'work'" v-show="$mq.isMobileOrTablet" />
  </AppPage>
</template>

<script setup>
const props = defineProps({});
const brandRow = useBrandRow();
const route = useRoute();
const prismic = usePrismic();
const isLoaded = useAppLoaded();
const { $gsap: gsap, $mq } = useNuxtApp();

const { data: work } = await useAsyncData(route.params.id, () =>
  prismic.client.getByUID('work', route.params.id)
);

const { data: works } = await useAsyncData('works', () =>
  prismic.client.getSingle('works')
);

const siteUrl = 'https://priscofrancesco.com';
const workData = computed(() => work.value?.data ?? {});
const workTitle = computed(() =>
  workData.value.name
    ? `${workData.value.name} | ${meta.titleShort}`
    : meta.title
);
const normalizeText = value => value?.replace(/\s+/g, ' ').trim();
const recognitions = computed(() =>
  workData.value.recognitions?.filter(item => item?.platform && item?.label) ?? []
);
const recognitionText = computed(() =>
  recognitions.value
    .map(item => normalizeText(`${item.platform} ${item.label}`))
    .join(', ')
);
const generatedDescription = computed(() => {
  if (!workData.value.name) return meta.description;

  const details = [
    workData.value.role && `Role: ${workData.value.role}`,
    workData.value.agency && `Agency: ${workData.value.agency}`,
    workData.value.year && `Year: ${workData.value.year}`,
    recognitionText.value && `Recognitions: ${recognitionText.value}`,
  ].filter(Boolean);

  return normalizeText(
    `${workData.value.name} is a digital experience by Francesco Prisco. ${details.join('. ')}.`
  );
});
const workDescription = computed(
  () => normalizeText(workData.value.description) || generatedDescription.value
);
const workUrl = computed(() => `${siteUrl}${route.path}`);
const workImage = computed(() => workData.value.image?.url || `${siteUrl}${meta.image}`);

const nextProject = computed(() => {
  if (!works.value?.data?.works || !work.value) return null;

  const currentIndex = works.value.data.works.findIndex(
    w => w.work.uid === route.params.id
  );

  if (currentIndex === -1) return null;

  const wrapper = gsap.utils.wrap(0, works.value.data.works.length);
  const nextIndex = wrapper(currentIndex + 1);
  return {
    ...works.value.data.works[nextIndex],
    work: {
      ...works.value.data.works[nextIndex].work,
      index: nextIndex,
    },
  };
});

definePageMeta({
  pageTransition: {
    name: 'page',
    mode: 'out-in',
  },
});

useSeoMeta({
  title: workTitle.value,
  ogTitle: workTitle.value,
  description: workDescription.value,
  ogDescription: workDescription.value,
  ogImage: workImage.value,
  twitterTitle: workTitle.value,
  twitterDescription: workDescription.value,
  twitterImage: workImage.value,
});

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CreativeWork',
        '@id': `${workUrl.value}#creative-work`,
        name: workData.value.name,
        url: workUrl.value,
        description: workDescription.value,
        image: workImage.value,
        dateCreated: workData.value.year,
        creator: {
          '@id': `${siteUrl}/#francesco-prisco`,
        },
        contributor: workData.value.agency
          ? {
              '@type': 'Organization',
              name: workData.value.agency,
            }
          : undefined,
        award: recognitionText.value || undefined,
        genre: workData.value.role,
        sameAs: workData.value.link?.url || undefined,
      }),
    },
  ],
});

watch(
  () => isLoaded.value,
  () => {
    if (isLoaded.value) {
      gsap.delayedCall(0.5, () => {
        brandRow.value = 'top';
      });
    }
  },
  { immediate: true }
);
onMounted(() => {
  if (!isLoaded.value) return;
  brandRow.value = 'top';
});
</script>

<style lang="scss" scoped>
.work {
  min-height: var(--height);
  opacity: 0;
}
</style>
